import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Camera, Images, RefreshCw, X, Loader2, Check, RotateCcw, ArrowRight, Info, SkipForward,
} from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { VIEWS, SUBSCORE_LABELS } from "../lib/data";
import { api } from "../lib/api";

const LABEL_COLOR = { good: "#2E8540", moderate: "#E67300", poor: "#D9381E" };

function ScoreRing({ score }) {
  const r = 46, c = 2 * Math.PI * r;
  const color = score >= 70 ? "#2E8540" : score >= 55 ? "#E67300" : "#D9381E";
  return (
    <svg width="120" height="120" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r={r} fill="none" stroke="#EDEBE5" strokeWidth="10" />
      <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * Math.min(100, score)) / 100} transform="rotate(-90 60 60)" />
      <text x="60" y="58" textAnchor="middle" fontSize="30" fontWeight="800" fill="#1A1C1B" fontFamily="Work Sans">{Math.round(score)}</text>
      <text x="60" y="76" textAnchor="middle" fontSize="11" fill="#6B6D6C">/ 100</text>
    </svg>
  );
}

export default function Capture() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("sid");
  const step = parseInt(params.get("step") || "0", 10);
  const view = VIEWS[step];

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState("");
  const [showGuide, setShowGuide] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!sid || !view) return navigate(`/mode?sid=${sid || ""}`);
    setShowGuide(true);
    setResult(null);
  }, [step]);

  useEffect(() => {
    let active = true;
    setCamReady(false);
    setCamError("");
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setCamReady(true);
      } catch (e) {
        setCamError("Camera unavailable. Use Gallery to upload a photo.");
      }
    })();
    return () => { active = false; if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [facing, step]);

  const next = () => {
    if (step < VIEWS.length - 1) navigate(`/capture?sid=${sid}&step=${step + 1}`);
    else navigate(`/addxray?sid=${sid}`);
  };

  const uploadBlob = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("view_type", view.key);
      fd.append("file", blob, `${view.key}.jpg`);
      const r = await api.post(`/screenings/${sid}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 1080;
    canvas.height = v.videoHeight || 1440;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((b) => b && uploadBlob(b), "image/jpeg", 0.92);
  };

  const onPick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadBlob(f); };

  if (!view) return null;

  return (
    <div className="min-h-screen w-full flex justify-center bg-black">
      <div className="relative w-full max-w-md min-h-screen bg-black flex flex-col">
        {/* Camera */}
        <div className="relative flex-1 bg-black overflow-hidden">
          <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />
          {/* Alignment frame */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-[80%] h-[46%] rounded-[38%] border-2 border-dashed border-white/70" />
          </div>
          {/* Top bar */}
          <div className="absolute top-0 inset-x-0 p-4 flex items-start justify-between bg-gradient-to-b from-black/70 to-transparent">
            <button data-testid="capture-close" onClick={() => navigate(`/mode?sid=${sid}`)} className="p-2 rounded-full bg-black/40 text-white"><X className="w-5 h-5" /></button>
            <div className="text-center text-white">
              <p className="font-head font-bold text-sm">{view.title}</p>
              <p className="text-[11px] text-white/80">Step {step + 1} of {VIEWS.length}</p>
            </div>
            <button data-testid="capture-guide-btn" onClick={() => setShowGuide(true)} className="p-2 rounded-full bg-black/40 text-white"><Info className="w-5 h-5" /></button>
          </div>
          {/* Step dots */}
          <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
            {VIEWS.map((_, i) => (<span key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-[#8FE3BE]" : i < step ? "bg-white/70" : "bg-white/30"}`} />))}
          </div>
          {camError && <div className="absolute inset-0 flex items-center justify-center p-8 text-center text-white/90 text-sm bg-black/60">{camError}</div>}
          {busy && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>}
        </div>

        {/* Bottom control bar */}
        <div className="bg-black px-6 py-5 flex items-center justify-between">
          <button data-testid="capture-gallery" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-1 text-white/90 text-[11px] w-16">
            <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center"><Images className="w-5 h-5" /></div>
            Gallery
          </button>
          <button data-testid="capture-shutter" onClick={snap} disabled={!camReady || busy}
            className="tap w-[74px] h-[74px] rounded-full bg-white ring-4 ring-white/30 flex items-center justify-center disabled:opacity-40">
            <Camera className="w-8 h-8 text-black" />
          </button>
          <button data-testid="capture-skip" onClick={next} className="flex flex-col items-center gap-1 text-white/90 text-[11px] w-16">
            <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center"><SkipForward className="w-5 h-5" /></div>
            Skip
          </button>
        </div>
        <div className="bg-black pb-3 flex justify-center">
          <button data-testid="capture-flip" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} className="flex items-center gap-1.5 text-white/70 text-xs">
            <RefreshCw className="w-3.5 h-3.5" /> Flip camera
          </button>
        </div>

        <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="capture-file-input" />

        {/* How-to guide overlay */}
        {showGuide && (
          <div data-testid="capture-guide" className="absolute inset-0 z-40 bg-black/80 flex items-end">
            <div className="w-full bg-background rounded-t-3xl p-6 pb-8 fade-up">
              <div className="mx-auto w-full h-40 rounded-2xl bg-secondary overflow-hidden mb-4 flex items-center justify-center">
                <img src={`/guides/${view.key}.jpg`} alt={`How to take the ${view.title} photo`} className="w-full h-full object-contain" />
              </div>
              <h2 className="font-head font-bold text-xl tracking-tight text-center">{view.title}</h2>
              <p className="text-sm text-muted-foreground text-center mb-4">{view.instruction}</p>
              <ol className="flex flex-col gap-2.5 mb-6">
                {view.steps.map((s, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="text-sm">{s}</span>
                  </li>
                ))}
              </ol>
              <button data-testid="capture-guide-gotit" onClick={() => setShowGuide(false)}
                className="tap w-full h-13 py-3.5 rounded-2xl brand-gradient text-white font-semibold">Got it</button>
            </div>
          </div>
        )}

        {/* Quality result sheet */}
        <Sheet open={!!result} onOpenChange={(v) => !v && setResult(null)}>
          <SheetContent side="bottom" data-testid="quality-sheet" className="rounded-t-3xl max-w-md mx-auto">
            <SheetTitle className="sr-only">Photo quality</SheetTitle>
            {result && (
              <div className="flex flex-col gap-4 pb-4">
                <div className="flex items-center gap-4">
                  <ScoreRing score={result.quality_score} />
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full"
                      style={{ background: result.quality_status === "accept" ? "#E6F2EA" : "#FCEBE8", color: result.quality_status === "accept" ? "#1E5C2C" : "#9E2A16" }}>
                      {result.quality_status === "accept" ? "Good quality" : "Consider retaking"}
                    </span>
                    <p className="font-head font-bold text-lg mt-1">{view.title}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 bg-secondary/60 rounded-2xl p-4">
                  {Object.entries(result.subscores).map(([k, v]) => {
                    const lbl = result.labels[k];
                    return (
                      <div key={k} className="flex items-center gap-3">
                        <span className="text-xs w-20 text-muted-foreground">{SUBSCORE_LABELS[k]}</span>
                        <div className="flex-1 h-2 rounded-full bg-background overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${v}%`, background: LABEL_COLOR[lbl] }} />
                        </div>
                        <span className="text-[11px] w-16 text-right font-medium capitalize" style={{ color: LABEL_COLOR[lbl] }}>{lbl}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3">
                  <button data-testid="quality-retake" onClick={() => setResult(null)}
                    className="tap flex-1 h-12 rounded-2xl border border-border bg-card font-semibold flex items-center justify-center gap-1.5">
                    <RotateCcw className="w-4 h-4" /> Retake
                  </button>
                  <button data-testid="quality-accept" onClick={() => { setResult(null); next(); }}
                    className="tap flex-1 h-12 rounded-2xl brand-gradient text-white font-semibold flex items-center justify-center gap-1.5">
                    {step < VIEWS.length - 1 ? (<><Check className="w-4 h-4" /> Accept & next</>) : (<>Finish <ArrowRight className="w-4 h-4" /></>)}
                  </button>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
